// Imports
import { faPen, faPhone, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components/macro';
import ButtonGroup from '../../../../../../../shareables/foundation/front-end/components/buttons/group';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIButton, { APIButtonOptions } from '../../../../../../../shareables/integrations/api/components/button';
import theme from '../../../../../../../shareables/theme';
import { GetAddressesResponse } from '../../../../../../../shareables/types/api/main/website/account/addresses/get';
import formatPhoneNumber from '../../../../../../../shareables/utils/format-phone-number';


// Styled components
const Column = styled.div`
	flex: none;
	width: 33.3333333%;
	padding: 0.75rem;
	
	@media (max-width: ${theme.breakpoints.widescreen}px) {
		width: 50%;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		width: 100%;
	}
`;

const Card = styled.div`
	background: #eee;
	border-radius: 0.1875rem;
	border: 0.0625rem solid #ddd;
	padding: 0.9375rem;
`;

const FormattedLines = styled.div`
	line-height: 1.3125rem;
	overflow-wrap: break-word;
`;

const AddressNotes = styled.div`
	padding-top: 0.3125rem;
	line-height: 1.125rem;
	
	.svg-inline--fa {
		margin-right: 0.3125rem;
	}
`;

const Buttons = styled(ButtonGroup)`
	margin-top: 1rem;
`;


// Define the accepted props
interface Props {
	address: GetAddressesResponse[0];
	onChange: () => void;
	onClick: () => void;
}


// Function component
const Address: React.FC<Props> = ({ address, onChange, onClick }) => {
	// Use Redux functionality
	const callingCode = useAppSelector((state) => state.self.website?.country.callingCode);
	
	
	// Build button options
	const buttonOptions: APIButtonOptions<undefined, undefined, undefined> = {
		action: 'DELETE',
		uri: `/website/${window.location.hostname}/account/address/${address.id}`,
		warning: {
			message: 'Deleting this address is permanent.',
			button: `Yes, delete “${address.formatted.split('\n')[0]}”`,
		},
	};
	
	
	// Return JSX
	return (
		<Column>
			<Card>
				<FormattedLines>
					{address.formatted.split('\n').map((line, i) => (
						<div key={i}>{line}</div>
					))}
				</FormattedLines>
				
				<AddressNotes>
					{address.extraAddressDetail && (
						<div>
							<small>
								<em>
									{address.extraAddressDetail.label}: {address.extraAddressDetail.value}
								</em>
							</small>
						</div>
					)}
					
					{address.homePhone && (
						<div>
							<small>
								<em>
									<FontAwesomeIcon icon={faPhone} size='sm' />
									{formatPhoneNumber(address.homePhone, callingCode ?? 1)}
								</em>
							</small>
						</div>
					)}
					
					{(address.mailingAddress || address.billingAddress) && (
						<div>
							<small>
								<em>
									{address.mailingAddress && 'Mailing address'}
									{address.mailingAddress && address.billingAddress && ', '}
									{address.billingAddress && 'Billing address'}
								</em>
							</small>
						</div>
					)}
				</AddressNotes>
				
				<Buttons>
					<Button type='button' color='plain' border smaller onClick={onClick}>
						<FontAwesomeIcon icon={faPen} />
						<span>Edit</span>
					</Button>
					
					<APIButton options={buttonOptions} onCompletion={onChange} color='danger' smaller>
						<FontAwesomeIcon icon={faTrash} />
						<span>Delete</span>
					</APIButton>
				</Buttons>
			</Card>
		</Column>
	);
};

export default Address;
